import { LeftOutlined } from '@ant-design/icons';
import { getDealRoomInfo } from '@app/api/deal-room-info.api';
import { getDealRoom } from '@app/api/deal-room.api';
import { Card } from '@app/components/common/Card/Card';
import { ADMIN_BASE_BATH } from '@app/constants/variables';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { useResponsive } from '@app/hooks/useResponsive';
import { setDealRoom, setDealRoomInfo } from '@app/store/slices/dealRoomSlice';
import { Col, Row } from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DealRoomInfo } from './DealRoomCard/DealRoomInfo/DealRoomInfo';
import { DealRoomNav } from './DealRoomCard/DealRoomNav/DealRoomNav';
import * as S from './DealRoomLayout.styles';
import { AxiosError } from 'axios';

const DealRoomLayout: React.FC = () => {
  const dealRoom = useAppSelector((state) => state.dealRoom.dealRoom);

  const { id } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const getTitle = () => {
    return id ? t('dealRooms.form.updateTitle') : t('dealRooms.form.addTitle');
  };

  const { t } = useTranslation();
  const { isTablet: isTabletOrHigher, mobileOnly } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();

  const isTitleShown = isTabletOrHigher || (mobileOnly && location.pathname === '/deal-rooms');

  useEffect(() => {
    async function fetchDealRoom(id: string) {
      try {
        const dealRoom = await getDealRoom(id);
        dispatch(setDealRoom(dealRoom));
      } catch (error) {
        notificationController.error({ message: (error as Error).message });
        if ((error as AxiosError).status === 403) {
          navigate(`${ADMIN_BASE_BATH}/deal-rooms`);
        }
      }
    }

    async function fetchDealRoomInfo(id: string) {
      try {
        const dealRoomInfo = await getDealRoomInfo(id);
        dispatch(setDealRoomInfo(dealRoomInfo));
      } catch (error) {}
    }

    if (id) {
      fetchDealRoom(id);
      fetchDealRoomInfo(id);
    }

    return () => {
      dispatch(setDealRoom(null));
      dispatch(setDealRoomInfo(null));
    };
  }, [dispatch, id, navigate]);

  return (
    <>
      {!isTitleShown && (
        <S.Btn icon={<LeftOutlined />} type="text" onClick={() => navigate(`${ADMIN_BASE_BATH}/deal-rooms`)}>
          {t('common.back')}
        </S.Btn>
      )}
      <Card
        title={
          <S.FlexWrapper>
            <S.Title>{getTitle()}</S.Title>
            {dealRoom?.barozRoomNumber && (
              <S.SubTitle>
                {t('dealRooms.form.barOzId')}: <b>{dealRoom?.barozRoomNumber}</b>
              </S.SubTitle>
            )}
          </S.FlexWrapper>
        }
        padding="1.25rem"
      >
        <Row gutter={[30, 30]}>
          <Col xs={24} md={24} xl={8}>
            <S.ProfileCard>
              <Row gutter={[30, 30]}>
                <Col xs={24} md={12} xl={24}>
                  <DealRoomInfo dealRoom={dealRoom} />
                </Col>

                <Col xs={24} md={12} xl={24}>
                  <DealRoomNav />
                </Col>
              </Row>
            </S.ProfileCard>
          </Col>
          <Col xs={24} md={24} xl={16}>
            <Outlet />
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default DealRoomLayout;
